<template>
  <b-card>
    <layout-loading v-if="itemLoading" />
    <b-form v-if="render">
      <b-row>
        <b-col md="6">
          <b-form-group
            :label="$t('general.title')"
            label-for="mom-experience-title"
          >
            <b-form-input
              id="mom-experience-title"
              v-model="form.title"
              :state="validateState('title')"
            />
            <b-form-invalid
              id="input-title-feedback"
              :label="$t('general.title')"
              :validator="$v.form.title"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.slug')"
            label-for="mom-experience-slug"
          >
            <b-form-input
              id="mom-experience-slug"
              v-model="form.slug"
              :state="validateState('slug')"
            />
            <b-form-invalid
              id="input-slug-feedback"
              :label="$t('general.slug')"
              :validator="$v.form.slug"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.language')"
            label-for="mom-experience-lang"
          >
            <language-selector
              v-model="form.lang"
              @change-selected-language="changeSelectedLanguage"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.status')"
            label-for="mom-experience-status"
            :state="validateState('status')"
          >
            <b-form-select v-model="form.status">
              <b-form-select-option
                v-for="(status, index) in statusOptions"
                :key="index"
                :value="status"
              >
                {{ $t(`articles.${status}`) }}
              </b-form-select-option>
            </b-form-select>
            <b-form-invalid
              id="input-status-feedback"
              :label="$t('general.status')"
              :validator="$v.form.status"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.tags')"
            label-for="mom-experience-tag"
            :state="validateState('tags')"
          >
            <v-select-custom
              id="mom-experience-tags"
              v-model="form.tags"
              :custom-props="{ multiple: true, taggable: true }"
              :options="tags"
              @search="fetchTags(form.lang, $event)"
              @option:created="addNewTag(form.lang, $event)"
            />
            <b-form-invalid
              id="input-tags-feedback"
              :label="$t('general.tags')"
              :validator="$v.form.tags"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.approver')"
            label-for="mom-experience-approver"
          >
            <v-select
              id="article-approver"
              v-model="form.approver"
              dir="rtl"
              label="firstName"
              :options="approverOptions"
              @search="fetchApprovers(form.lang, $event)"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.managerImageFileId"
                  :title="option.firstName + option.lastName"
                />
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('articles.relatedArticles')"
            label-for="mom-experience-related-articles"
            :state="validateState('relatedArticles')"
          >
            <v-select
              id="mom-experience-related-articles"
              v-model="form.relatedArticles"
              dir="rtl"
              multiple
              label="title"
              :options="articleOptions"
            >
              <template #selected-option="option">
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title[form.lang]"
                />
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <v-select-option
                  :value="option.articleThumbnailFileId"
                  :title="option.title[form.lang]"
                />
              </template>
            </v-select>
            <b-form-invalid
              id="input-relatedArticles-feedback"
              :label="$t('articles.relatedArticles')"
              :validator="$v.form.relatedArticles"
              :is-bootstrap="false"
            />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            :label="$t('general.priority')"
            label-for="mom-experience-sort"
          >
            <b-form-input
              id="mom-experience-sort"
              v-model.number="form.sort"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.keywords')"
            label-for="mom-experience-keywords"
          >
            <b-form-input
              id="mom-experience-keywords"
              v-model="form.keywords"
              :state="validateState('keywords')"
            />
            <b-form-invalid
              id="input-keywords-feedback"
              :label="$t('general.keywords')"
              :validator="$v.form.keywords"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group
            :label="$t('general.showOnHome')"
            label-for="mom-experience-showOnHome"
            class="mb-2"
          >
            <b-form-checkbox
              id="mom-experience-showOnHome"
              class="mt-1"
              :checked="form.showOnHome"
              name="mom-experience-showOnHome"
              switch
              inline
              @input="handleShowOnHomeChange"
            />
          </b-form-group>
        </b-col>
        <b-col md="12">
          <b-form-group
            :label="$t('general.description')"
            label-for="mom-experience-description"
          >
            <b-form-textarea
              id="mom-experience-description"
              v-model="form.description"
              :state="validateState('description')"
              rows="6"
              aria-describedby="input-description-feedback"
            />
            <b-form-invalid
              id="input-description-feedback"
              :label="$t('general.description')"
              :validator="$v.form.description"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="12"
          class="collapse-border"
        >
          <b-form-group
            label-for="mom-experience-content"
            class="mb-2"
          >
            <CkeditorBasic
              id="mom-experience-content"
              v-model="form.content"
              :label="$t('general.content')"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          class="mb-2"
        >
          <b-file-input
            id="momExperienceThumbnail"
            v-model="form.momExperienceThumbnail"
            :label="$t('general.thumbnail') + ' ' + $t('general.acceptImage')"
            :placeholder="$t('general.select')"
            :state="validateState('momExperienceThumbnail')"
            :alt.sync="form.momExperienceThumbnailAlt"
            :alt-validator="$v.form.momExperienceThumbnailAlt"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-mom-experience-thumbnail-feedback"
                :label="$t('general.thumbnail')"
                :validator="$v.form.momExperienceThumbnail"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col cols="6">
          <b-file-input
            id="momExperienceVideo"
            v-model="form.momExperienceVideo"
            :label="$t('general.video') + ' ' + $t('general.acceptVideo')"
            :placeholder="$t('general.select')"
            :state="validateState('momExperienceVideo')"
            :accept="'.mp4,.mov,.flv,.mkv'"
            :alt.sync="form.momExperienceVideoAlt"
            :alt-validator="$v.form.momExperienceVideoAlt"
            type="video"
          >
            <template #invalidFeedback>
              <b-form-invalid
                id="input-mom-experience-video-feedback"
                :label="$t('general.video')"
                :validator="$v.form.momExperienceVideo"
              />
            </template>
          </b-file-input>
        </b-col>
        <b-col
          cols="12"
          class="mt-50"
        >
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            class="mr-1"
            :disabled="isSubmit"
            @click="submitForm"
          >
            {{ isSubmit ? $t("general.isSubmit") : $t("general.submit") }}
          </b-button>
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            @click="$router.go(-1)"
          >
            {{ $t("general.back") }}
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>
<script>
import {
  BButton,
  BCard,
  BCol,
  BForm,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormSelectOption,
  BFormTextarea,
  BRow,
} from 'bootstrap-vue'
import BFileInput from '@/components/BFileInput/index.vue'
import BFormInvalid from '@/components/BFormInvalid/index.vue'
import CkeditorBasic from '@/components/CkeditorBasic/index.vue'
import LayoutLoading from '@/components/LayoutLoading.vue'
import Ripple from 'vue-ripple-directive'
import VSelectCustom from '@/components/VSelectCustom/index.vue'
import VSelectOption from '@/components/VSelectCustom/VSelectOption.vue'
import languageSelector from '@/components/language-selector.vue'
import vSelect from 'vue-select'
import { required, minLength } from 'vuelidate/lib/validators'
import useMomExperiencesList from './useMomExperiencesList'

export default {
  name: 'MomExperienceEdit',
  components: {
    BButton,
    BCard,
    BCol,
    BFileInput,
    BForm,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormInvalid,
    BFormSelect,
    BFormSelectOption,
    BFormTextarea,
    BRow,
    CkeditorBasic,
    LayoutLoading,
    VSelectCustom,
    VSelectOption,
    languageSelector,
    vSelect,
  },
  directives: {
    Ripple,
  },
  validations() {
    return {
      form: {
        title: { required, minLength: minLength(3) },
        slug: { required, isUnique: () => this.isUniqueSlug },
        tags: { required },
        keywords: { required, minLength: minLength(3) },
        description: { required, minLength: minLength(10) },
        momExperienceThumbnail: { required },
        momExperienceVideo: { required },
        momExperienceVideoAlt: { required },
        momExperienceThumbnailAlt: { required, minLength: minLength(3) },
        relatedArticles: {},
        content: {},
        status: { required },
      },
    }
  },

  setup() {
    const {
      addItem,
      addNewTag,
      approverOptions,
      articleOptions,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchItem,
      fetchTags,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      statusOptions,
      tags,
      updateItem,
    } = useMomExperiencesList()

    return {
      addItem,
      addNewTag,
      approverOptions,
      articleOptions,
      checkSlugIsUnique,
      convertResource,
      createTagIdArray,
      errorManager,
      fetchApprovers,
      fetchArticles,
      fetchItem,
      fetchTags,
      form,
      handleUpload,
      isSubmit,
      isUniqueSlug,
      item,
      itemLoading,
      newTagIds,
      statusOptions,
      tags,
      updateItem,
    }
  },
  computed: {
    itemId() {
      return this.$router.currentRoute.params.id
    },
    render() {
      return this.item ? this.item && Object.keys(this.item).length : true
    },
  },
  watch: {
    'form.slug': function () {
      this.checkSlugIsUnique(this.form.lang, this.form.slug)
    },
  },
  created() {
    this.fetchItem(this.itemId).then(() => {
      this.fetchTags(this.form.lang)
      this.fetchApprovers()
      this.fetchArticles(this.form.lang)
    })
  },
  methods: {
    changeSelectedLanguage(selectedLang) {
      this.form = this.convertResource(this.item, selectedLang)
      this.fetchArticles(selectedLang)
      this.fetchTags(this.form.lang)
    },
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name]
      return $dirty ? !$error : null
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.errorManager('validator.validationError')
        setTimeout(() => document.getElementsByClassName('is-invalid')[0]
          .scrollIntoView({ behavior: 'smooth', block: 'center' }), 500)
        return
      }
      const uploadList = [
        'momExperienceThumbnail',
        'momExperienceVideo',
      ]
      this.handleUpload(this.form, uploadList).then(newForm => {
        this.sendData(newForm)
      })
        .catch(err => {
          if (err.response.data.message[0] === 'property file should not exist') {
            this.sendData(this.form)
          }
        })
    },
    sendData(form) {
      const data = {
        title: form.title,
        slug: form.slug,
        keywords: form.keywords,
        description: form.description,
        tagIds: this.createTagIdArray(this.form.tags, this.newTagIds),
        approverId: form.approver ? form.approver._id : null,
        relatedArticleIds: form.relatedArticles ? form.relatedArticles.map(item => item._id) : [],
        momExperienceThumbnailFileId: form.momExperienceThumbnail && form.momExperienceThumbnail._id,
        momExperienceVideoFileId: form.momExperienceVideo && form.momExperienceVideo._id,
        status: form.status,
        content: form.content,
        showOnHome: form.showOnHome,
        sort: form.sort,
      }
      this.updateItem(this.itemId, data, this.form.lang)
    },
    handleShowOnHomeChange(value) {
      this.$set(this.form, 'showOnHome', value)
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss"
</style>
